import { saveAs } from "file-saver";
import { toPng, toSvg } from "html-to-image";
import { getRectOfNodes, getTransformForBounds, type Node } from "reactflow";

type ExportImageType = "png" | "svg";

/**
 * Empty margin left around the nodes, in px, so that the outermost frames and
 * their connection dots don't end up flush against the image edge.
 */
const exportPadding = 60;

/**
 * The element React Flow draws the nodes and the edges into.
 *
 * It's a sibling of the grid, the zoom controls and the attribution, so
 * exporting it alone leaves all of the canvas chrome out of the image without
 * having to filter anything.
 */
const viewportSelector = ".react-flow__viewport";

/**
 * Ensures all nodes have concrete width and height (from DOM if needed)
 * so getRectOfNodes calculates the true full bounding box without clipping.
 */
const enrichNodesWithDomDimensions = (nodes: Node[]): Node[] => {
  return nodes.map((node) => {
    let width = node.width ?? 0;
    let height = node.height ?? 0;
    if (width === 0 || height === 0) {
      const domNode = document.querySelector<HTMLElement>(
        `.react-flow__node[data-id="${node.id}"]`,
      );
      if (domNode !== null) {
        width = width > 0 ? width : domNode.offsetWidth;
        height = height > 0 ? height : domNode.offsetHeight;
      }
    }
    return {
      ...node,
      width: width > 0 ? width : 220,
      height: height > 0 ? height : 120,
    };
  });
};

/**
 * How long to wait for the image before giving up, in ms.
 *
 * Rendering goes through the browser's own image decoding, which can stall
 * indefinitely on a very large graph or a background tab. Without a deadline
 * the export button would simply stay disabled forever with nothing to tell
 * the user why.
 */
const exportTimeout = 30000;

const withTimeout = async (
  promise: Promise<string>,
  timeout: number,
): Promise<string> => {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const deadline = new Promise<string>((_resolve, reject) => {
    timeoutId = setTimeout(() => {
      reject(
        new Error(
          `rendering took longer than ${Math.round(
            timeout / 1000,
          )}s, the graph may be too large`,
        ),
      );
    }, timeout);
  });

  try {
    return await Promise.race([promise, deadline]);
  } finally {
    clearTimeout(timeoutId);
  }
};

/**
 * Saves the graph as an image file.
 *
 * The nodes are exported at their own scale rather than at the current zoom, so
 * the file looks the same no matter how far the canvas happens to be zoomed in
 * or out when the user asks for it.
 *
 * Note on SVG: `html-to-image` wraps the live HTML in a `<foreignObject>`, so
 * the file is a valid SVG that any browser renders, but its contents are still
 * HTML rather than editable vector shapes.
 *
 * @param imageType Which format to write.
 * @param nodes React Flow nodes, used to frame the image.
 * @param backgroundColor Color to paint behind the nodes, so a PNG isn't
 * transparent where the canvas used to be.
 * @throws Error when there's nothing to export or the canvas isn't mounted.
 */
const exportGraphImage = async (
  imageType: ExportImageType,
  nodes: Node[],
  backgroundColor: string,
): Promise<void> => {
  if (nodes.length === 0) {
    throw new Error("There are no nodes to export");
  }

  const viewport = document.querySelector<HTMLElement>(viewportSelector);
  if (viewport === null) {
    throw new Error("Could not find the graph to export");
  }

  const enrichedNodes = enrichNodesWithDomDimensions(nodes);
  const nodesBounds = getRectOfNodes(enrichedNodes);
  const width = Math.ceil(nodesBounds.width) + exportPadding * 2;
  const height = Math.ceil(nodesBounds.height) + exportPadding * 2;
  // Pinning both the min and the max zoom to 1 keeps the export at 1:1 and
  // leaves the transform to do nothing but center the nodes
  const [x, y, zoom] = getTransformForBounds(nodesBounds, width, height, 1, 1);

  // Copy SVG defs/markers from .react-flow parent into viewport SVG edges so markers resolve in clone
  const reactFlowRoot = viewport.closest(".react-flow");
  const markerDefs = reactFlowRoot?.querySelectorAll(
    "svg defs, svg.react-flow__markers",
  );
  const edgesSvg = viewport.querySelector<SVGSVGElement>(
    "svg.react-flow__edges",
  );
  let temporaryDefsGroup: SVGGElement | null = null;

  if (edgesSvg !== null && markerDefs !== undefined && markerDefs.length > 0) {
    temporaryDefsGroup = document.createElementNS(
      "http://www.w3.org/2000/svg",
      "g",
    );
    markerDefs.forEach((elem) => {
      temporaryDefsGroup?.appendChild(elem.cloneNode(true));
    });
    edgesSvg.insertBefore(temporaryDefsGroup, edgesSvg.firstChild);
  }

  try {
    const options = {
      backgroundColor,
      width,
      height,
      style: {
        width: `${width}px`,
        height: `${height}px`,
        transform: `translate(${x}px, ${y}px) scale(${zoom})`,
      },
    };

    const dataUrl = await withTimeout(
      imageType === "png" ? toPng(viewport, options) : toSvg(viewport, options),
      exportTimeout,
    );

    const response = await fetch(dataUrl);
    const blob = await response.blob();
    saveAs(blob, `computational-graph.${imageType}`);
  } finally {
    temporaryDefsGroup?.remove();
  }
};

export { exportGraphImage };
export type { ExportImageType };
